import { useState, useEffect, useCallback } from 'react';
import { outcomeApi } from '../api/outcomeApi';
import { toast } from '../utils/toast';

export const useOutcomes = (options = {}) => {
  const { autoFetch = true } = options;
  const [outcomes, setOutcomes] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchOutcomes = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await outcomeApi.getOutcomes(params);

      // Handle different response formats
      let outcomeData = [];
      let paginationData = { page: 1, limit: 10, total: 0, totalPages: 0 };

      if (response && response.data) {
        if (response.pagination) {
          // Standardized format: { data: [...], pagination: {...} }
          outcomeData = Array.isArray(response.data) ? response.data : [];
          paginationData = response.pagination;
        } else if (response.data.data && response.data.pagination) {
          // Alternative format
          outcomeData = Array.isArray(response.data.data) ? response.data.data : [];
          paginationData = response.data.pagination;
        } else if (Array.isArray(response.data)) {
          outcomeData = response.data;
          paginationData.total = response.data.length;
          paginationData.totalPages = 1;
        }
      } else if (Array.isArray(response)) {
        outcomeData = response;
        paginationData.total = response.length;
        paginationData.totalPages = 1;
      }

      setOutcomes(outcomeData);
      setPagination(paginationData);
    } catch (err) {
      setError(err.message || 'Failed to fetch outcomes');
      setOutcomes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const createOutcome = useCallback(async (outcomeData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await outcomeApi.createOutcome(outcomeData);

      // Handle response format
      let newOutcome = null;
      if (response && response.data && response.data.data) {
        newOutcome = response.data.data;
      } else if (response && response.data) {
        newOutcome = response.data;
      } else if (response) {
        newOutcome = response;
      }

      // Do not push partial outcome into list; rely on refetch by caller
      if (newOutcome) toast.success('Outcome created successfully!');

      return newOutcome;
    } catch (err) {
      const errorMessage = err.response?.data?.responseMessage || err.response?.data?.error || err.message || 'Failed to create outcome';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateOutcome = useCallback(async (outcomeId, outcomeData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await outcomeApi.updateOutcome(outcomeId, outcomeData);

      // Handle response format
      let updatedOutcome = null;
      if (response && response.data && response.data.data) {
        updatedOutcome = response.data.data;
      } else if (response && response.data) {
        updatedOutcome = response.data;
      } else if (response) {
        updatedOutcome = response;
      }

      // Do not locally mutate; caller will refetch

      return updatedOutcome;
    } catch (err) {
      setError(err.message || 'Failed to update outcome');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteOutcome = useCallback(async (outcomeId) => {
    try {
      setLoading(true);
      setError(null);
      await outcomeApi.deleteOutcome(outcomeId);
      // Let caller refetch
    } catch (err) {
      setError(err.message || 'Failed to delete outcome');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const approveOutcome = useCallback(async (outcomeId, data = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await outcomeApi.approveOutcome(outcomeId, data);

      // Handle response format
      let updatedOutcome = null;
      if (response && response.data && response.data.data) {
        updatedOutcome = response.data.data;
      } else if (response && response.data) {
        updatedOutcome = response.data;
      } else if (response) {
        updatedOutcome = response;
      }

      // Let caller refetch.

      return updatedOutcome;
    } catch (err) {
      setError(err.message || 'Failed to approve outcome');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const rejectOutcome = useCallback(async (outcomeId, data = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await outcomeApi.rejectOutcome(outcomeId, data);

      // Handle response format
      let updatedOutcome = null;
      if (response && response.data && response.data.data) {
        updatedOutcome = response.data.data;
      } else if (response && response.data) {
        updatedOutcome = response.data;
      } else if (response) {
        updatedOutcome = response;
      }

      // Let caller refetch

      return updatedOutcome;
    } catch (err) {
      setError(err.message || 'Failed to reject outcome');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!autoFetch) return;
    fetchOutcomes();
  }, [fetchOutcomes, autoFetch]);

  return {
    outcomes,
    pagination,
    loading,
    error,
    fetchOutcomes,
    createOutcome,
    updateOutcome,
    deleteOutcome,
    approveOutcome,
    rejectOutcome,
    // React Query compatibility
    refetch: fetchOutcomes,
    data: outcomes,
    isLoading: loading,
    isError: !!error
  };
};

// Export individual hooks for direct import
export const useCreateOutcome = () => {
  const { createOutcome, loading, error } = useOutcomes({ autoFetch: false });
  return {
    createOutcome,
    loading,
    error,
    // React Query compatibility
    mutateAsync: createOutcome,
    mutate: createOutcome,
    isPending: loading,
    isError: !!error
  };
};

export const useUpdateOutcome = () => {
  const { updateOutcome, loading, error } = useOutcomes({ autoFetch: false });
  return {
    updateOutcome,
    loading,
    error,
    // React Query compatibility
    mutateAsync: updateOutcome,
    mutate: updateOutcome,
    isPending: loading,
    isError: !!error
  };
};

export const useDeleteOutcome = () => {
  const { deleteOutcome, loading, error } = useOutcomes({ autoFetch: false });
  return {
    deleteOutcome,
    loading,
    error,
    // React Query compatibility
    mutateAsync: deleteOutcome,
    mutate: deleteOutcome,
    isPending: loading,
    isError: !!error
  };
};

export const useApproveOutcome = () => {
  const { approveOutcome, loading, error } = useOutcomes({ autoFetch: false });
  return {
    approveOutcome,
    loading,
    error,
    // React Query compatibility
    mutateAsync: approveOutcome,
    mutate: approveOutcome,
    isPending: loading,
    isError: !!error
  };
};

export const useRejectOutcome = () => {
  const { rejectOutcome, loading, error } = useOutcomes({ autoFetch: false });
  return {
    rejectOutcome,
    loading,
    error,
    // React Query compatibility
    mutateAsync: rejectOutcome,
    mutate: rejectOutcome,
    isPending: loading,
    isError: !!error
  };
};
